import { AsyncPipe, CommonModule } from "@angular/common";
import { Store } from "@ngxs/store";
// tslint:disable

/*
 * PK : Adapted this file to include popovers in the padding area
 * And expose the top property of this scroll area to make up in position the overlay
 *
 */

import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from "@angular/core";

import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { PlanningState } from "../store/planning.state";

export interface ChangeEvent {
  start?: number;
  end?: number;
}

@Component({
  selector: "virtual-scroll,[virtualScroll]",
  exportAs: "virtualScroll",
  standalone: true,
  imports: [CommonModule, AsyncPipe],
  template: `
    <div class="total-padding" #shim>
      <ng-content select=".overlay"></ng-content>
    </div>
    <div
      class="scrollable-content"
      [style.width]="contentWidth | async"
      #content
    >
      <ng-content select=".content"></ng-content>
    </div>
  `,
  host: {
    "[style.overflow-y]": "parentScroll ? 'hidden' : 'auto'",
  },
  styles: [
    `
      :host {
        overflow: hidden;
        position: relative;
        display: block;
        -webkit-overflow-scrolling: touch;
      }
      .scrollable-content {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        position: absolute;
      }
      .total-padding {
      }
    `,
  ],
})
export class VirtualScrollComponent implements OnInit, OnChanges, OnDestroy {
  public visibleCandidatesAmount$: Observable<number>;

  @Input()
  items: any[] = [];

  @Input()
  scrollbarWidth: number;

  @Input()
  scrollbarHeight: number;

  @Input()
  childWidth: number;

  @Input()
  childHeight: number;

  @Input()
  bufferAmount: number = 0;

  @Input()
  scrollAnimationTime: number = 1500;

  @Input()
  doNotCheckAngularZone: boolean = false;

  private refreshHandler = () => {
    this.refresh();
  };
  private _parentScroll: Element | Window;
  @Input()
  set parentScroll(element: Element | Window) {
    if (this._parentScroll === element) {
      return;
    }
    this._parentScroll = element;
    this.addParentEventHandlers(this._parentScroll);
  }

  get parentScroll(): Element | Window {
    return this._parentScroll;
  }

  @Output()
  update: EventEmitter<any[]> = new EventEmitter<any[]>();
  viewPortItems: any[];

  @Output()
  change: EventEmitter<ChangeEvent> = new EventEmitter<ChangeEvent>();

  @Output()
  start: EventEmitter<ChangeEvent> = new EventEmitter<ChangeEvent>();

  @Output()
  end: EventEmitter<ChangeEvent> = new EventEmitter<ChangeEvent>();

  @ViewChild("content", { read: ElementRef, static: true })
  contentElementRef: ElementRef;

  @ViewChild("shim", { read: ElementRef, static: true })
  shimElementRef: ElementRef;

  @ContentChild("container", { static: true })
  containerElementRef: ElementRef;

  previousStart: number;
  previousEnd: number;
  startupLoop: boolean = true;
  // Removed currentTween property - no longer needed without tween.js

  // FIXME :PK public property to get overlays to work
  topPadding: number;

  private disposeScrollHandler: () => void | undefined;
  private disposeResizeHandler: () => void | undefined;

  /** Cache of the last scroll height to prevent setting CSS when not needed. */
  private lastScrollHeight = -1;

  /** Cache of the last top padding to prevent setting CSS when not needed. */
  private lastTopPadding = -1;

  constructor(
    private readonly element: ElementRef,
    private readonly renderer: Renderer2,
    private readonly zone: NgZone,
    private store: Store
  ) {}

  ngOnInit() {
    this.scrollbarWidth = 0; // this.element.nativeElement.offsetWidth - this.element.nativeElement.clientWidth;
    this.scrollbarHeight = 0; // this.element.nativeElement.offsetHeight - this.element.nativeElement.clientHeight;
    this.visibleCandidatesAmount$ = this.store
      .select(PlanningState.filteredCandidates)
      .pipe(map((candidates) => candidates?.length || 0));

    if (!this.parentScroll) {
      this.addParentEventHandlers(this.element.nativeElement);
    }
  }

  ngOnDestroy() {
    this.removeParentEventHandlers();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.previousStart = undefined;
    this.previousEnd = undefined;
    const items = (changes as any).items || {};
    if (
      ((changes as any).items != undefined &&
        items.previousValue == undefined) ||
      (items.previousValue != undefined && items.previousValue.length === 0)
    ) {
      this.startupLoop = true;
    }
    this.refresh();
  }

  refresh(forceViewportUpdate: boolean = false) {
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => this.calculateItems(forceViewportUpdate));
    });
  }

  scrollInto(item: any) {
    let el: Element =
      this.parentScroll instanceof Window
        ? document.body
        : this.parentScroll || this.element.nativeElement;
    let offsetTop = this.getElementsOffset();
    let index: number = (this.items || []).indexOf(item);
    if (index < 0 || index >= (this.items || []).length) return;

    let d = this.calculateDimensions();
    let scrollTop =
      Math.floor(index / d.itemsPerRow) * d.childHeight -
      d.childHeight * Math.min(index, this.bufferAmount);

    let animationRequest;

    // No longer need to stop tween animation

    // totally disable animate
    if (!this.scrollAnimationTime) {
      el.scrollTop = scrollTop;
      return;
    }

    // Use native smooth scrolling instead of tween.js to avoid CSP eval() issues
    if (el.scrollTo) {
      el.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });
    } else {
      // Fallback for older browsers - simple linear animation
      const startScrollTop = el.scrollTop;
      const distance = scrollTop - startScrollTop;
      const startTime = performance.now();

      const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / this.scrollAnimationTime, 1);

        // Quadratic easing out function (similar to tween.Easing.Quadratic.Out)
        const easeOut = 1 - Math.pow(1 - progress, 2);

        const newScrollTop = startScrollTop + distance * easeOut;

        this.renderer.setProperty(el, "scrollTop", newScrollTop);
        this.refresh();

        if (progress < 1) {
          this.zone.runOutsideAngular(() => {
            requestAnimationFrame(animateScroll);
          });
        }
      };

      this.zone.runOutsideAngular(() => {
        requestAnimationFrame(animateScroll);
      });
    }
  }

  private addParentEventHandlers(parentScroll: Element | Window) {
    this.removeParentEventHandlers();
    if (parentScroll) {
      this.zone.runOutsideAngular(() => {
        this.disposeScrollHandler = this.renderer.listen(
          parentScroll,
          "scroll",
          this.refreshHandler
        );
        if (parentScroll instanceof Window) {
          this.disposeScrollHandler = this.renderer.listen(
            "window",
            "resize",
            this.refreshHandler
          );
        }
      });
    }
  }

  private removeParentEventHandlers() {
    if (this.disposeScrollHandler) {
      this.disposeScrollHandler();
      this.disposeScrollHandler = undefined;
    }
    if (this.disposeResizeHandler) {
      this.disposeResizeHandler();
      this.disposeResizeHandler = undefined;
    }
  }

  private countItemsPerRow() {
    let offsetTop;
    let itemsPerRow;
    let children = this.contentElementRef.nativeElement.children;
    for (itemsPerRow = 0; itemsPerRow < children.length; itemsPerRow++) {
      if (
        offsetTop != undefined &&
        offsetTop !== children[itemsPerRow].offsetTop
      )
        break;
      offsetTop = children[itemsPerRow].offsetTop;
    }
    return itemsPerRow;
  }

  public getElementsOffset(): number {
    let offsetTop = 0;
    if (this.containerElementRef && this.containerElementRef.nativeElement) {
      offsetTop += this.containerElementRef.nativeElement.offsetTop;
    }
    if (this.parentScroll) {
      offsetTop += this.element.nativeElement.offsetTop;
    }
    return offsetTop;
  }

  private calculateDimensions() {
    let el: Element =
      this.parentScroll instanceof Window
        ? document.body
        : this.parentScroll || this.element.nativeElement;
    let items = this.items || [];
    let itemCount = items.length;
    let viewWidth = el.clientWidth - this.scrollbarWidth;
    let viewHeight = el.clientHeight - this.scrollbarHeight;

    let contentDimensions;
    if (this.childWidth == undefined || this.childHeight == undefined) {
      let content = this.contentElementRef.nativeElement;
      if (this.containerElementRef && this.containerElementRef.nativeElement) {
        content = this.containerElementRef.nativeElement;
      }
      contentDimensions = content.children[0]
        ? content.children[0].getBoundingClientRect()
        : {
            width: viewWidth,
            height: viewHeight,
          };
    }
    let childWidth = this.childWidth || contentDimensions.width;
    let childHeight = this.childHeight || contentDimensions.height;

    let itemsPerRow = Math.max(1, this.countItemsPerRow());
    let itemsPerRowByCalc = Math.max(1, Math.floor(viewWidth / childWidth));
    let itemsPerCol = Math.max(1, Math.floor(viewHeight / childHeight));
    let elScrollTop =
      this.parentScroll instanceof Window
        ? window.scrollY ||
          document.documentElement.scrollTop ||
          document.body.scrollTop ||
          0
        : el.scrollTop;
    let scrollTop = Math.max(0, elScrollTop);
    const scrollHeight = childHeight * Math.ceil(itemCount / itemsPerRow);
    if (
      itemsPerCol === 1 &&
      Math.floor((scrollTop / scrollHeight) * itemCount) + itemsPerRowByCalc >=
        itemCount
    ) {
      itemsPerRow = itemsPerRowByCalc;
    }

    if (scrollHeight !== this.lastScrollHeight) {
      this.renderer.setStyle(
        this.shimElementRef.nativeElement,
        "height",
        `${scrollHeight}px`
      );
      this.lastScrollHeight = scrollHeight;
    }

    return {
      itemCount: itemCount,
      viewWidth: viewWidth,
      viewHeight: viewHeight,
      childWidth: childWidth,
      childHeight: childHeight,
      itemsPerRow: itemsPerRow,
      itemsPerCol: itemsPerCol,
      itemsPerRowByCalc: itemsPerRowByCalc,
      scrollHeight,
    };
  }

  private calculateItems(forceViewportUpdate: boolean = false) {
    if (!this.doNotCheckAngularZone) {
      NgZone.assertNotInAngularZone();
    }
    let el =
      this.parentScroll instanceof Window
        ? document.body
        : this.parentScroll || this.element.nativeElement;

    let d = this.calculateDimensions();
    let items = this.items || [];
    let offsetTop = this.getElementsOffset();
    let elScrollTop =
      this.parentScroll instanceof Window
        ? window.scrollY ||
          document.documentElement.scrollTop ||
          document.body.scrollTop ||
          0
        : el.scrollTop;

    if (elScrollTop > d.scrollHeight) {
      elScrollTop = d.scrollHeight + offsetTop;
    }

    let scrollTop = Math.max(0, elScrollTop - offsetTop);
    let indexByScrollTop =
      ((scrollTop / d.scrollHeight) * d.itemCount) / d.itemsPerRow;
    let end = Math.min(
      d.itemCount,
      Math.ceil(indexByScrollTop) * d.itemsPerRow +
        d.itemsPerRow * (d.itemsPerCol + 1)
    );

    let maxStartEnd = end;
    const modEnd = end % d.itemsPerRow;
    if (modEnd) {
      maxStartEnd = end + d.itemsPerRow - modEnd;
    }
    let maxStart = Math.max(
      0,
      maxStartEnd - d.itemsPerCol * d.itemsPerRow - d.itemsPerRow
    );
    let start = Math.min(
      maxStart,
      Math.floor(indexByScrollTop) * d.itemsPerRow
    );

    this.topPadding =
      items == null || items.length === 0
        ? 0
        : d.childHeight * Math.ceil(start / d.itemsPerRow) -
          d.childHeight * Math.min(start, this.bufferAmount);

    if (this.topPadding !== this.lastTopPadding) {
      this.renderer.setStyle(
        this.contentElementRef.nativeElement,
        "transform",
        `translateY(${this.topPadding}px)`
      );
      this.renderer.setStyle(
        this.contentElementRef.nativeElement,
        "webkitTransform",
        `translateY(${this.topPadding}px)`
      );
      this.lastTopPadding = this.topPadding;
    }

    start = !isNaN(start) ? start : -1;
    end = !isNaN(end) ? end : -1;
    start -= this.bufferAmount;
    start = Math.max(0, start);
    end += this.bufferAmount;
    end = Math.min(items.length, end);
    if (
      start !== this.previousStart ||
      end !== this.previousEnd ||
      forceViewportUpdate === true
    ) {
      this.zone.run(() => {
        // update the scroll list
        let _end = end >= 0 ? end : 0; // To prevent from accidentally selecting the entire array with a negative 1 (-1) in the end position.
        this.viewPortItems = items.slice(start, _end);
        this.update.emit(this.viewPortItems);

        // emit 'start' event
        if (start !== this.previousStart && this.startupLoop === false) {
          this.start.emit({ start, end });
        }

        // emit 'end' event
        if (end !== this.previousEnd && this.startupLoop === false) {
          this.end.emit({ start, end });
        }

        this.previousStart = start;
        this.previousEnd = end;

        if (this.startupLoop === true) {
          this.refresh();
        } else {
          this.change.emit({ start, end });
        }
      });
    } else if (this.startupLoop === true) {
      this.startupLoop = false;
      this.refresh();
    }
  }

  get contentWidth(): Observable<string> {
    return this.visibleCandidatesAmount$.pipe(
      map((length) => 400 + 48 * length + "px")
    );
  }
}

// Removed NgModule - use VirtualScrollComponent directly as standalone component
